// ─── « Notifications » — how each alert reaches you (2026-10-01) ────────────
//
// Plan docs/superpowers/plans/2026-10-01-pwa-push.md, piece 4. One row per
// alert type with a four-way choice — Push / WhatsApp / Both / Bell — grouped
// by how much the alert needs the owner. The choices belong to the signed-in
// user (worker routes/push.ts); the rules live in ./notificationPrefs.ts.
//
// MOBILE FIRST: the owner mostly opens this from the installed app. At 375 px
// a row is its label above a full-width control of four 44 px targets; from
// `sm` up the label sits to the left of the control.
//
// A tap shows at once and saves behind it. A refused save puts the row back
// to what the worker holds and says why, on that row.
import { Fragment, useId, useReducer, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { pushPrefsApi, type AlertGroup, type Channel, type PushPrefKind } from '@/lib/pushPrefsApi';
import {
  CHANNELS, defaultChannel, editsReducer, groupKinds, lockReason, shownChannel,
  type Edits, type PrefGroup, type RowEdit,
} from './notificationPrefs';

const errorText = (e: unknown) => (e instanceof Error && e.message ? e.message : 'unknown error');
const channelLabel = (c: Channel) => CHANNELS.find((x) => x.id === c)?.label ?? c;

const LockIcon = ({ className = 'w-3.5 h-3.5' }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round" className={`${className} shrink-0`} aria-hidden="true"
  >
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

export function NotificationPrefsPanel() {
  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['push-prefs'],
    queryFn: () => pushPrefsApi.prefs(),
    // ⚠️ Forgotten as soon as the screen closes. Saves are not written back
    // into this cache, so the next visit must ask the worker, not show the
    // list as it was when the screen first opened.
    gcTime: 0,
  });
  const [edits, dispatch] = useReducer(editsReducer, {});
  const seq = useRef(new Map<string, number>());
  const queue = useRef(new Map<string, Promise<void>>());

  const choose = (k: PushPrefKind, group: AlertGroup, channel: Channel) => {
    if (lockReason(group, channel) || shownChannel(edits, k) === channel) return;
    const n = (seq.current.get(k.kind) ?? 0) + 1;
    seq.current.set(k.kind, n);
    const latest = () => seq.current.get(k.kind) === n;
    dispatch({ type: 'choose', kind: k.kind, channel, from: k.channel });
    // ⚠️ ONE SAVE AT A TIME PER ROW, IN TAP ORDER. Two quick taps sent side by
    // side could land in either order and leave the worker on the older one
    // while the screen shows the newer.
    const run = (queue.current.get(k.kind) ?? Promise.resolve())
      .then(() => pushPrefsApi.setPref(k.kind, channel))
      .then(
        () => dispatch({ type: 'saved', kind: k.kind, channel, latest: latest() }),
        (e: unknown) => dispatch({ type: 'failed', kind: k.kind, error: errorText(e), latest: latest() }),
      );
    queue.current.set(k.kind, run);
  };

  if (isPending) return <LoadingCards />;
  if (isError) return <LoadError message={errorText(error)} retrying={isFetching} onRetry={() => { void refetch(); }} />;

  return (
    <div className="space-y-4">
      <ChoicesLegend />
      {groupKinds(data.kinds ?? []).map((g) => (
        <GroupCard
          key={g.id}
          group={g}
          edits={edits}
          onChoose={(k, channel) => choose(k, g.id, channel)}
          // Only the rows not already on the default are saved (choose skips the rest).
          onReset={() => { for (const k of g.kinds) choose(k, g.id, defaultChannel(g.id)); }}
        />
      ))}
    </div>
  );
}

/** What the four choices mean — once, above the groups. */
function ChoicesLegend() {
  return (
    <div className="rounded-xl border border-border-subtle bg-bg-card px-4 py-3">
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[12.5px] leading-snug">
        {CHANNELS.map((c) => (
          <Fragment key={c.id}>
            <dt className="font-semibold text-text-primary">{c.label}</dt>
            <dd className="text-text-secondary">{c.hint}</dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}

function GroupCard({ group, edits, onChoose, onReset }: {
  group: PrefGroup & { kinds: PushPrefKind[] };
  edits: Edits;
  onChoose: (k: PushPrefKind, channel: Channel) => void;
  onReset: () => void;
}) {
  const titleId = useId();
  const lockId = useId();
  const def = defaultChannel(group.id);
  // The group's locked choice, said once under its title (the system group).
  const lock = CHANNELS.map((c) => lockReason(group.id, c.id)).find((r) => r) ?? null;
  const atDefaults = group.kinds.every((k) => shownChannel(edits, k) === def);

  return (
    <section aria-labelledby={titleId} className="bg-white rounded-xl border border-gray-200">
      <div className="px-4 pt-3.5 pb-3 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-[15px] font-semibold leading-snug text-text-primary pt-0.5">{group.title}</h2>
          <button
            type="button"
            onClick={onReset}
            disabled={atDefaults}
            title={atDefaults ? 'Already on the defaults' : undefined}
            className="shrink-0 -mr-2 -mt-2 md:-mt-1 px-2 min-h-11 md:min-h-8 rounded-lg text-[12px] font-semibold whitespace-nowrap
              text-brand-700 hover:bg-brand-50 cursor-pointer transition-colors duration-150
              focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500
              disabled:text-text-tertiary disabled:hover:bg-transparent disabled:cursor-default"
          >
            Reset to defaults
          </button>
        </div>
        <p className="text-[12.5px] text-text-secondary mt-0.5 leading-snug">{group.explanation}</p>
        {lock && (
          <p id={lockId} className="mt-1.5 flex items-start gap-1.5 text-[12px] text-text-secondary leading-snug">
            <LockIcon className="w-3.5 h-3.5 mt-px text-text-tertiary" />
            <span>{lock}</span>
          </p>
        )}
      </div>

      {group.kinds.length === 0 ? (
        <p className="px-4 py-3.5 text-[12px] text-text-tertiary">No alert types here yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {group.kinds.map((k) => (
            <PrefRow
              key={k.kind}
              kind={k}
              group={group.id}
              value={shownChannel(edits, k)}
              edit={edits[k.kind]}
              lockId={lockId}
              onChoose={(channel) => onChoose(k, channel)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function PrefRow({ kind, group, value, edit, lockId, onChoose }: {
  kind: PushPrefKind;
  group: AlertGroup;
  value: Channel;
  edit: RowEdit | undefined;
  lockId: string;
  onChoose: (channel: Channel) => void;
}) {
  return (
    <li className="px-4 py-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 flex-1 flex items-baseline justify-between gap-2">
          <p className="text-[13.5px] font-medium leading-snug text-text-primary">{kind.label}</p>
          {edit?.saving && <span className="shrink-0 text-[11px] text-text-tertiary">Saving…</span>}
        </div>
        <ChannelControl label={kind.label} group={group} value={value} lockId={lockId} onChoose={onChoose} />
      </div>
      {edit?.error && (
        <p role="alert" className="mt-2 text-[12px] leading-snug text-error">
          Not saved — {edit.error.replace(/[.\s]+$/, '')}. Still on {channelLabel(value)}.
        </p>
      )}
    </li>
  );
}

/** The four-way segmented control. The group's default carries « default ». */
function ChannelControl({ label, group, value, lockId, onChoose }: {
  label: string;
  group: AlertGroup;
  value: Channel;
  lockId: string;
  onChoose: (channel: Channel) => void;
}) {
  const def = defaultChannel(group);
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="grid grid-cols-4 gap-0.5 w-full sm:w-[22rem] shrink-0 rounded-lg border border-border-subtle bg-bg-card p-0.5"
    >
      {CHANNELS.map((c) => {
        const locked = lockReason(group, c.id);
        const on = value === c.id;
        const isDefault = c.id === def;
        return (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={isDefault ? `${c.label} (default)` : c.label}
            aria-describedby={locked ? lockId : undefined}
            title={locked ?? undefined}
            disabled={!!locked}
            onClick={() => onChoose(c.id)}
            className={[
              'min-h-11 px-0.5 min-[360px]:px-1 rounded-md flex flex-col items-center justify-center gap-px leading-tight',
              'transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              on
                ? 'bg-accent-soft text-text-primary font-semibold shadow-[inset_0_0_0_1px_rgba(201,160,76,0.55)]'
                : 'text-text-secondary',
              locked ? 'opacity-50 cursor-not-allowed' : on ? 'cursor-pointer' : 'cursor-pointer hover:bg-bg-elevated hover:text-text-primary',
            ].join(' ')}
          >
            {/* 11 px below 360 px wide: there a cell is ~60 px and a bold
                « WhatsApp » at 12.5 px is 63. */}
            <span className="text-[11px] min-[360px]:text-[12.5px]">{c.label}</span>
            {/* Second line: « default », or the lock — under the label, never
                beside it: at 375 px a cell is ~74 px and « WhatsApp » fills it. */}
            {isDefault && (
              <span className="text-[9.5px] font-semibold uppercase tracking-[0.06em] text-brand-700">default</span>
            )}
            {locked && !isDefault && <LockIcon className="w-3 h-3" />}
          </button>
        );
      })}
    </div>
  );
}

function LoadingCards() {
  return (
    <div role="status" className="space-y-4" aria-busy="true" aria-label="Loading your notification settings">
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
          <div className="h-3.5 w-48 max-w-full rounded bg-gray-100" />
          <div className="h-2.5 w-64 max-w-full rounded bg-gray-50 mt-2" />
          <div className="h-11 rounded-lg bg-gray-50 mt-4" />
        </div>
      ))}
    </div>
  );
}

function LoadError({ message, retrying, onRetry }: { message: string; retrying: boolean; onRetry: () => void }) {
  return (
    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
      <p className="text-[13px] font-semibold text-red-800">Couldn’t load your notification settings.</p>
      <p className="text-[12px] text-red-700 mt-0.5 break-words">{message}</p>
      <Button size="sm" variant="secondary" className="mt-3" onClick={onRetry} loading={retrying}>
        Try again
      </Button>
    </div>
  );
}
